// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DefaultGaugePropsDefinedSchema.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import {
  createDropDownSchema,
  createPopupSettingSchema,
  createCheckBoxSchema,
  createSelectSchema,
  createNumberSchema,
  createColorPaletteSettingSchema,
  createOrientationSettingsSchema
} from 'share/utils/Izenda.PropsEditorGuiSchemaFunctions';

import ChartDefaultConfig from 'logic/data/Izenda.ChartDefaultConfig';

import { getAll } from 'share/factories/Izenda.ReportPartStyleFactories';
import { REPORT_PART_TYPE } from 'logic/data/Izenda.ReportDesignerConstants';

import { DefaultChartType, CHART_TYPES, CHART_SCHEMA_NAMES } from 'logic/data/Izenda.ReportPartChartConstants';
import localizer from 'share/localization/Izenda.Localizer';

import ChartBorderSettings from 'modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ChartBorderSettings';
import ChartDataRefreshSettings from 'modules/ReportDesigner/Fields/PropertiesBuilder/Izenda.DataRefreshSetting';

const { CHART_KEY, GROUPS, FIELDS } = CHART_SCHEMA_NAMES;

const getChartTypeOptions = () =>
  getAll(REPORT_PART_TYPE.Gauge).map(({ label, style, previewCSS }) => ({
    label,
    value: style,
    className: previewCSS
  }));

/**
 * The common chart properties GUI schema that shares to all gauge type.
 */
export default {
  schemaKey: 'RP_GAUGE_TYPE',
  groups: {
    generalInfo: {
      title: () => localizer.getText('REPORT_PART_GAUGE_PROPS_SCHEMA_LABEL_GENERAL_INFO'),
      fields: {
        chartType: {
          factory: createDropDownSchema,
          title: () => localizer.getText('REPORT_PART_GAUGE_PROPS_SCHEMA_LABEL_GAUGE_TYPE'),
          value: 'getChartType',
          props: {
            get options() {
              return getChartTypeOptions();
            }
          }
        }
      }
    },
    gauge: {
      title: () => localizer.getText('COMMON_LABEL_GAUGE'),
      isHidden: 'isNotBuild',
      hiddenByState: true,
      fields: {
        izBorder: {
          factory: createPopupSettingSchema,
          title: () => localizer.getText('REPORT_PART_GAUGE_PROPS_SCHEMA_LABEL_BORDER'),
          value: 'getBorder',
          props: {
            get reportPartType() {
              return REPORT_PART_TYPE.Gauge;
            },
            get title() {
              return localizer.getText('REPORT_PART_GAUGE_PROPS_SCHEMA_LABEL_GAUGE_BORDER_SETTINGS');
            },
            hiddenByState: true,
            allChartProps: 'getAllChartProps',
            hadSetting: 'hadSettingBorder',
            fnResetDefaultValue: 'fnResetBorderDefaultValue',
            loadValue: 'getSettingBorder'
          },
          staticProps: {
            get popupTitle() {
              return localizer.getText('REPORT_PART_GAUGE_PROPS_SCHEMA_LABEL_GAUGE_BORDER_SETTINGS');
            },
            popupContent: ChartBorderSettings
          }
        },
        // Intervals: number input
        izIntervals: {
          factory: createNumberSchema,
          title: () => localizer.getText('REPORT_PART_GAUGE_PROPS_SCHEMA_LABEL_INTERVALS'),
          info: () => localizer.getText('REPORT_PART_GAUGE_PROPS_SCHEMA_LABEL_INTERVAL_OF_TICK'),
          isHidden: 'hideByChartType',
          value: 'getValueByKey'
        },
        colorTheme: {
          factory: createColorPaletteSettingSchema,
          title: () => localizer.getText('COMMON_LABEL_COLOR_THEME'),
          value: 'getColorTheme',
          props: {
            get selectionPopupTitle() {
              return localizer.getText('COLORTHEME_POPUP_HEADER');
            },
            get defaultColorThemeLabel() {
              return localizer.getText('COLORPALETTE_NOTHEME');
            },
            displaySettingStatus: true,
            needMaskDefault: false,
            defaultColorPalette: null,
            hiddenByState: true
          }
        }
      }
    },
    grouping: {
      title: () => localizer.getText('REPORT_PART_GAUGE_PROPS_SCHEMA_LABEL_GROUPING'),
      isHidden: 'isNotBuild',
      className: 'getClassNameByChartStatus',
      fields: {
        izUseSeparator: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('COMMON_LABEL_USE_SEPARATOR'),
          value: 'getValueByKey'
        }
      }
    },
    view: {
      title: () => localizer.getText('MODEL_LABEL_VIEW'),
      isHidden: 'isNotBuild',
      fields: {
        showLabels: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_GAUGE_PROPS_SCHEMA_LABEL_SHOW_LABEL'),
          value: 'getValueByKey'
        },
        izInverted: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_GAUGE_PROPS_SCHEMA_LABEL_INVERTED'),
          isHidden: 'hideByChartType',
          value: 'getValueByKey'
        },
        izInverted: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_GAUGE_PROPS_SCHEMA_LABEL_INVERTED'),
          isHidden: 'hideByChartType',
          value: 'getValueByKey'
        },
        dataRefreshInterval: {
          factory: createPopupSettingSchema,
          title: () => localizer.getText('COMMON_LABEL_DATA_REFRESH_INTERVAL_SETTING'),
          value: 'getValueByKey',
          props: {
            get reportPartType() {
              return REPORT_PART_TYPE.Gauge;
            },
            get title() {
              return localizer.getText('COMMON_LABEL_DATA_REFRESH_INTERVAL_SETTING');
            },
            hadSetting: 'hadSettingRefreshInterval',
            fnResetDefaultValue: 'fnResetRefreshIntervalDefaultValue',
            hiddenByState: true,
            loadValue: 'getSettingRefreshInterval'
          },
          staticProps: {
            get popupTitle() {
              return localizer.getText('COMMON_LABEL_DATA_REFRESH_INTERVAL_SETTING');
            },
            popupContent: ChartDataRefreshSettings,
            popupProps: {
              type: 'none',
              autoClose: false
            }
          }
        },
        izUsePagination: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_GAUGE_PROPS_SCHEMA_LABEL_USE_PAGINATION'),
          value: 'getValueByKey'
        },
        izItemPerRow: {
          factory: createNumberSchema,
          title: () => localizer.getText('REPORT_PART_GAUGE_PROPS_SCHEMA_LABEL_USE_PAGINATION_ITEM_PER_ROW'),
          value: 'getValueByKey',
          props: {
            min: 1,
            max: 20,
            isDecimal: false
          }
        }
      }
    },
    printing: {
      title: () => localizer.getText('REPORT_PART_GAUGE_PROPS_SCHEMA_LABEL_PRINTING'),
      isHidden: 'isNotBuild',
      fields: {
        izPageBreakAfterSeparator: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_GAUGE_PROPS_SCHEMA_PAGE_BREAK_AFTER_SEPARATOR'),
          value: 'getValueByKey'
        }
      }
    }
  }
};



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartGauge/Izenda.DefaultGaugePropsDefinedSchema.js